import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';

export enum UserRole {
  MasterAdmin = 'Master Admin',
  CSAdmin = 'CS Admin',
  AllAdmin = 'All Admin',
  StoreStaff = 'Store Staff',
  StoreManager = 'Store Manager',
  SalesRep = 'Sales Rep',
  SalesAdmin = 'Sales Admin',
  CommissionAdmin = 'Commission Admin',
  MarketingAdmin = 'Marketing Admin',
  Accounting = 'Accounting',
  Posting = 'Posting',
  Template = 'Template',
  TemplateAdmin = 'Template Admin',
  Marketing = 'Marketing',
  GEMID = 'GEM ID',
  WatchAdmin = 'Watch Admin',
  IM = 'IM',
  Owner = 'Owner',
  Influencer = 'Influencer',
  Tech = 'Tech',
  /** Template + self-approves its own product entries + can push them to Shopify/eBay. */
  DualRole = 'Dual Role'
}

/** Dual Role builds on Template, so it always carries the Template role too. */
export function withImpliedRoles(roles: UserRole[]): UserRole[] {
  if (roles.includes(UserRole.DualRole) && !roles.includes(UserRole.Template)) {
    return [...roles, UserRole.Template];
  }
  return roles;
}

export enum UserStatus {
  Active = 'active',
  Pending = 'pending',
  Suspended = 'suspended',
  Deleted = 'deleted'
}

export interface User {
  uid: string;
  email: string;
  displayName: string;
  roles: UserRole[];
  createdAt: Timestamp;
  lastLogin: Timestamp;
  status: UserStatus;
  storeId: string | null;
  photoURL?: string | null;
  photoPosition?: {
    x: number;
    y: number;
    scale: number;
  } | null;
  // When true, this user sees the soundboard launcher in the app chrome and
  // hears any sound any other soundboard-enabled user triggers.
  soundboardAccess?: boolean;
  // When true, this user can use the Instant Connect push-to-talk intercom.
  instantConnectAccess?: boolean;
  // When true, this user can view/use the store-specific Group Calendar
  // (managed by Master Admin in User Management).
  storeCalendarAccess?: boolean;
  // When true, this user is a jeweler: they can open repairs and submit the
  // final quote back to the store from the repair detail page.
  jewelerAccess?: boolean;
  // When true, this user gets the Claire bubble and /claire. Master Admins always have it.
  claireAccess?: boolean;
  // When true (and the user has neither the traffic counter nor the customer queue),
  // the dashboard shows the Bulletin Board instead.
  bulletinBoardAccess?: boolean;
  // When true, this user sees the holiday character a Master Admin sends floating up the screen.
  // Master Admins always see it.
  floaterAccess?: boolean;
  // Per-user page exclusions: normalized route paths (e.g. ['/users','/pos'])
  // this user is blocked from even if a role would grant access. Hides the
  // sidebar link and blocks direct navigation.
  excludedPages?: string[];
}

export interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: Error | null;
  login: (identifier: string, password: string | null, options?: { smsVerified?: boolean; token?: string; userData?: any }) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  clearError: () => void;
  refreshUserData: () => Promise<void>;
}

// Form Schemas
export const LoginFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  remember: z.boolean().optional()
});

export const RegisterFormSchema = z.object({
  email: z.string()
    .email('Invalid email address')
    .refine((email) => {
      const allowedDomains = ['@pmrbuys.com', '@chorostandco.com'];
      return allowedDomains.some(domain => email.toLowerCase().endsWith(domain));
    }, {
      message: 'Email must be from @pmrbuys.com or @chorostandco.com domain'
    }),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  displayName: z.string().min(2, 'Display name must be at least 2 characters')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

export type LoginFormData = z.infer<typeof LoginFormSchema>;
export type RegisterFormData = z.infer<typeof RegisterFormSchema>; 